import { useEffect, useState } from 'react';

// Tracks how often / how recently each route is visited so the menu and the
// command palette can surface the user's most-used pages. State is per-browser
// (localStorage), best-effort: any storage failure degrades to "no history"
// rather than throwing.
const STORAGE_KEY = 'ph_nav_usage';
const UPDATED_EVENT = 'ph-nav-usage-updated';

function readUsage() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

// Record one visit to `path`. Call on navigation.
export function recordPageVisit(path) {
  if (!path) return;
  try {
    const usage = readUsage();
    const entry = usage[path] || { count: 0, last: 0 };
    usage[path] = { count: entry.count + 1, last: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
    // Notify same-tab listeners (the native 'storage' event only fires cross-tab).
    window.dispatchEvent(new Event(UPDATED_EVENT));
  } catch (e) {
    // ignore — usage tracking is non-essential
  }
}

// Most-used paths, highest first. Frequency is primary; recency breaks ties.
export function getFrequentPaths(limit = 4) {
  const usage = readUsage();
  return Object.entries(usage)
    .sort((a, b) => (b[1].count - a[1].count) || (b[1].last - a[1].last))
    .slice(0, limit)
    .map(([path]) => path);
}

// Reactive version for components: re-renders when usage changes (this tab or another).
export default function useFrequentPaths(limit = 4) {
  const [paths, setPaths] = useState(() => getFrequentPaths(limit));

  useEffect(() => {
    const refresh = () => setPaths(getFrequentPaths(limit));
    window.addEventListener(UPDATED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(UPDATED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [limit]);

  return paths;
}
